import React, {Fragment, useContext, useEffect, useState} from "react";
import {Card, CardBody, CardHeader, Col, Container, Row} from "reactstrap";
import Loader from "../../../../../Layout/Loader";
import axiosConfig from "../../../../../Config/axios";
import {Breadcrumbs, H6} from "../../../../../AbstractElements";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import {useParams} from "react-router";
import {useNavigate} from "react-router-dom";
import ButtonIcon from "../../../../../CommonElements/ButtonIcon";
import {BootstrapTabContainer, BootstrapTabContent} from "../../../../../CommonElements/BootstrapTabs";
import BoardNumberIndex from "../../BoardNumber/Component/BoardNumberIndex";
import {ActivityLog} from "../../../../SystemCore/ActivityLog";
import WalletTransactionsTable from "../../../Wallet/WalletTransaction/Component/WalletTransactionsTable";
import ParticipantsTable from "../../../Participant/Participant/Component/ParticipantsTable";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {Board} from "../../Types/BoardTypes";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import UpdateBoardForm from "../Component/UpdateForm";
import WinnerForm from "../Component/WinnerForm";
import BoardDashboard from "../Component/BoardDashboard";
import WalletContext from "../../../Wallet/Context";
import type {WalletContextType} from "../../../Wallet/Context/WalletContext.types";
import AdminZoneWallet from "../../../Wallet/Component/AdminZoneWallet";

const BoardShow = () => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [board, setBoard] = useState<Board | null>(null);
    const [reload, setReload] = useState<boolean>(false);
    const params = useParams();
    const navigate = useNavigate();
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const walletContext = useContext(WalletContext) as WalletContextType;
    const {errorHandler, hasPermission, authorizedUser} = authContext || {};
    const {getWalletInformation} = walletContext || {};
    const userRoles = (authorizedUser?.system_roles || []).map((role: string) => role?.toLowerCase());
    const isSuperAdminUser = userRoles.includes('super-admin');
    const winningNumber = board?.winning_number ?? board?.winning_board_number?.winning_number ?? board?.winning_board_number?.number;
    const boardRoute = getApplicationRoute('board', true);
    const boardSlots = board?.number_of_slots;
    const boardValue = board?.statistics?.value
        ?? board?.value
        ?? (typeof board?.booking_types?.[0]?.amount === 'number' && typeof board?.number_of_slots === 'number'
            ? board.booking_types[0].amount * board.number_of_slots
            : undefined);
    const participantsCount = board?.statistics?.participants_count ?? board?.participants_count ?? board?.number_of_participants;
    const soldNumbers = board?.statistics?.sold_numbers ?? board?.sold_numbers;
    const collectorName = board?.collectors_names ?? board?.collector_name ?? board?.collector?.name;
    const statusLabel = board?.is_locked ? 'Locked' : 'Open';

    const getDisplayValue = (value?: string | number | null) => {
        return value === undefined || value === null || value === '' ? '-' : value;
    };

    const boardDetails = [
        {label: 'Winning Number', value: getDisplayValue(winningNumber), size: 12},
        {label: 'Slots', value: getDisplayValue(boardSlots)},
        {label: 'Board Value', value: getDisplayValue(boardValue)},
        {label: 'Participants', value: getDisplayValue(participantsCount)},
        {label: 'Sold Numbers', value: getDisplayValue(soldNumbers)},
        {label: 'Collector', value: getDisplayValue(collectorName)},
        {label: 'Date', value: getDisplayValue(board?.date)},
    ];

    if (board?.winning_board_number?.participant) {
        boardDetails.splice(1, 0, {
            label: 'Winner',
            value: getDisplayValue(board?.winning_board_number?.participant.name),
            size: 12,
        });
    }

    useEffect(() => {
        setLoaderVisibility(true);
        axiosConfig
            .get(`${getApplicationRoute('board')}/${params.board}`)
            .then((response: { data: { data: Board; }; }) => {
                if (response?.data?.data) {
                    setBoard(response.data.data);
                } else {
                    navigate(boardRoute);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
                navigate(boardRoute);
            });

    }, [params.board, reload])

    const deleteBoard = () => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`/application/board/${params.board}`, {})
            .then((response: { data: { data: { id: number; }; }; }) => {
                if (response?.data?.data) {
                    navigate(boardRoute);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Board" parent="Application" subParent={'Boards'}
                         subParentLink={getApplicationRoute('board')} title={board?.name}/>
            <Container fluid={true}>
                <div className="user-profile">
                    <Loader visibility={loaderVisibility}/>
                    <Row>
                        <Col sm='12' lg='3'>
                            <Card className='hovercard text-center'>
                                <CardHeader className='social-profile'>
                                    <div className='d-flex align-items-center gap-2'>
                                    </div>
                                </CardHeader>
                                <div className='info'>
                                    <Row className='step3' data-intro='This is the your details'>
                                        <Col sm='12' lg='12' className='order-sm-0 order-xl-1'>
                                            <div className='user-designation'>
                                                <div className='title'>
                                                    <span>{board?.name}</span>
                                                </div>
                                                <span className='text-muted'>{`Status: ${statusLabel}`}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                                <CardBody>
                                    <Row className='g-2 mb-3'>
                                        {boardDetails.map((detail) => (
                                            <Col sm={`${detail.size ? detail.size : 6}`} key={detail.label}>
                                                <div className='border rounded p-2 h-100'>
                                                    <small className='text-muted d-block'>{detail.label}</small>
                                                    <H6>{detail.value}</H6>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>

                                    {board?.pointWallet?.last_transaction && board ? (
                                        getWalletInformation(board?.pointWallet)) : null}

                                    {board?.cashWallet?.last_transaction && board ? (
                                        getWalletInformation(board?.cashWallet)
                                    ) : null}

                                    {!(board?.cashWallet || board?.pointWallet) && (
                                        <AdminZoneWallet
                                            relationKey="board"
                                            relationId={board?.id || ''}
                                            onSuccess={() => setReload(!reload)}
                                        />
                                    )}
                                    <div className='social-network theme-form m-t-20 border-top pt-3'>
                                        <span className='f-w-600 text-danger'>Danger Zone</span>
                                        <ButtonIcon confirm={{confirm:true}} onclick={() => deleteBoard()} type={'delete'}
                                                    permission={'delete-board'}/>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col sm='12' lg='9'>
                            <Card className='hovercard'>
                                <CardBody>
                                    <BootstrapTabContainer>
                                        {hasPermission && hasPermission('read-board') && (
                                            <BootstrapTabContent title={'Dashboard'} icon={"icofont icofont-dashboard"}>
                                                {board && <BoardDashboard board={board}/>}
                                            </BootstrapTabContent>
                                        )}

                                        {hasPermission && hasPermission('read-board-number') && (
                                            <BootstrapTabContent title={'Board Numbers'}
                                                                 icon={'icofont icofont-ui-calendar'}>
                                                <BoardNumberIndex params={{board_id: board?.id}}
                                                                  isLocked={board?.is_locked}
                                                                  callBack={() => setReload(!reload)}/>
                                            </BootstrapTabContent>
                                        )}

                                        {hasPermission && hasPermission('read-participant') && (
                                            <BootstrapTabContent title={'Participants'}
                                                                 icon={'icofont icofont-ui-user-group'}>
                                                <ParticipantsTable
                                                    params={{board_id: board?.id}}
                                                    tableOptions={{view: true}}
                                                />
                                            </BootstrapTabContent>
                                        )}

                                        {board?.pointWallet?.id && hasPermission && hasPermission('read-wallet-transaction') && (
                                            <BootstrapTabContent title={'Point'}
                                                                 icon={'icofont icofont-bank-transfer'}>
                                                <WalletTransactionsTable
                                                    params={{wallet_id: board.pointWallet.id}}/>
                                            </BootstrapTabContent>
                                        )}

                                        {board?.is_locked && isSuperAdminUser ? (
                                            <BootstrapTabContent title={'Winner'} icon={'icofont icofont-trophy'}>
                                                <WinnerForm board={board}
                                                            callBack={() => setReload(prev => !prev)}/>
                                            </BootstrapTabContent>
                                        ) : (!board?.is_locked && hasPermission && hasPermission('update-board') ? (
                                            <BootstrapTabContent title={'Update'} icon={'icofont icofont-ui-edit'}>
                                                {board && (
                                                    <UpdateBoardForm
                                                        key={board.id}
                                                        board={board}
                                                        callBack={(updatedBoard) => {
                                                            setBoard(updatedBoard);
                                                            setReload(prev => !prev);
                                                        }}
                                                    />
                                                )}
                                            </BootstrapTabContent>
                                        ) : null)}

                                        {hasPermission && hasPermission('read-activity-log') && (
                                            <BootstrapTabContent title={'Activity Log'} icon={'icofont icofont-list'}>
                                                {board && <ActivityLog subjectType="board" subjectId={board.id}/>}
                                            </BootstrapTabContent>
                                        )}

                                    </BootstrapTabContainer>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Container>
        </Fragment>
    );
};

export default BoardShow;
